import React, { useState, useEffect } from 'react';
import { LOCAL_STORAGE_NAME } from 'js/constants/App';
import { useSelector } from 'react-redux';
import { useSFX } from 'js/hooks/useSFX';

import AppIcon from '../../../images/static/app_icon.png';
import AddIcon from '../../../images/static/ios_add_icon.jpg';
import ShareIcon from '../../../images/static/ios_share_icon.jpg';
import DotsIcon from '../../../images/static/ios_dots_icon.jpg';
import ViewMoreIcon from '../../../images/static/ios_viewmore_icon.png';

// import utils
import { isBrowserIOSSafari26 } from 'js/utils/iosDetection';

// import selectors
import { getTranslationById } from 'js/store/app/App.selectors';

// import icons
import IconCross from 'js/components/icon/Icon.cross';

const DISMISS_KEY = `${LOCAL_STORAGE_NAME}:ios-install-dismissed`;

let timer = null;

function setDismissedWithExpiry() {
  const now = new Date();
  const expiryTime = now.getTime() + 24 * 60 * 60 * 1000;

  const item = {
    dismissed: true,
    expiry: expiryTime,
  };

  localStorage.setItem(DISMISS_KEY, JSON.stringify(item));
}

function isDismissedAndValid() {
  const item = localStorage.getItem(DISMISS_KEY);

  if (!item) return false;

  try {
    const parsed = JSON.parse(item);
    const now = new Date().getTime();

    // Check if expired
    if (now > parsed.expiry) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }

    return parsed.dismissed;
  } catch (e) {
    localStorage.removeItem(DISMISS_KEY);
    return false;
  }
}

function PWAIosPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const { playSFX } = useSFX();

  const checkShouldShowPrompt = () => {
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) return false;

    const isDismissed = isDismissedAndValid();

    return !isDismissed;
  };

  useEffect(() => {
    timer = setTimeout(() => {
      if (checkShouldShowPrompt()) {
        setShowPrompt(true);
      }
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const dismiss = () => {
    playSFX('button_tap');
    setDismissedWithExpiry();
    setShowPrompt(false);
  };

  const promptText = {
    title: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_title', 'Install the app')),
    appName: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_app_name', "Let's Play Outside")),
    appUrl: useSelector((state) =>
      getTranslationById(state, 'pwa_ios_prompt_app_url', 'sesameworkshop.org/letsplayoutside')
    ),
    step1Text1: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_step_1_text_1', 'Press')),
    step1Text2: useSelector((state) =>
      getTranslationById(state, 'pwa_ios_prompt_step_1_text_2', 'in the browser toolbar')
    ),
    step2Text1: useSelector((state) =>
      getTranslationById(state, 'pwa_ios_prompt_step_2_text_1', 'Scroll down and pick')
    ),
    step2Text2: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_step_2_text_2', 'Add to Home Screen')),
    step3Text1: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_step_3_text_1', 'Look for the')),
    step3Text2: useSelector((state) =>
      getTranslationById(state, 'pwa_ios_prompt_step_3_text_2', 'icon on your home screen')
    ),
    stepViewMore: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_step_view_more', 'Tap ‘View More’')),
    stepDots: useSelector((state) => getTranslationById(state, 'pwa_ios_prompt_step_dots', 'Tap the three dots')),
    stepDotsBelow: useSelector((state) =>
      getTranslationById(state, 'pwa_ios_prompt_step_dots_below', 'Tap the three dots in the address bar below.')
    ),
  };

  const ios26 = isBrowserIOSSafari26();

  if (!showPrompt) return null;

  return (
    <div className="pwa-prompts pwa-prompts--ios">
      <div className="pwa-prompts__content">
        <button className="pwa-prompts__closeButton" aria-label="Close prompt" onClick={dismiss}>
          <IconCross color="black" />
        </button>
        <div className="pwa-prompts__heading">{promptText.title}</div>
        <div className="pwa-prompts__appinfo">
          <img src={AppIcon} alt="App icon" />
          <div>
            <span className="pwa-prompts__appname">{promptText.appName}</span>
            <span className="pwa-prompts__appurl">{promptText.appUrl}</span>
          </div>
        </div>
        <ul className="pwa-prompts__list">
          {ios26 && (
            <>
              <li>
                <div>1. {promptText.stepDotsBelow}</div>
                <div>
                  <img src={DotsIcon} alt="Dots icon" />
                </div>
              </li>
              <li>
                <div> 2. {promptText.step1Text1}</div>
                <div>
                  <img src={ShareIcon} alt="Share icon" />
                </div>
                <div>
                  <strong>{promptText.step1Text2}</strong>
                </div>
              </li>
              <li>
                <div>3. {promptText.stepViewMore}</div>
                <div>
                  <img src={ViewMoreIcon} alt="View More icon" />
                </div>
              </li>
              <li>
                <div>4. {promptText.step2Text1}</div>
                <div className="pwa-prompts__addtohome">
                  <div>
                    <img src={AddIcon} alt="Add icon" />
                  </div>
                  <div>{promptText.step2Text2}</div>
                </div>
              </li>
              <li>
                <div>5. {promptText.step3Text1}</div>
                <div>
                  <img src={AppIcon} alt="App icon" className="pwa-prompts__appicon--small" />
                </div>
                <div>
                  <strong>{promptText.step3Text2}</strong>
                </div>
              </li>
            </>
          )}

          {!ios26 && (
            <>
              <li>
                <div>1. {promptText.step1Text1}</div>
                <div>
                  <img src={ShareIcon} alt="Share icon" />
                </div>
                <div>
                  <strong>{promptText.step1Text2}</strong>
                </div>
              </li>
              <li>
                <div> 2. {promptText.step2Text1}</div>
                <div className="pwa-prompts__addtohome">
                  <div>
                    <img src={AddIcon} alt="Add icon" />
                  </div>
                  <div>{promptText.step2Text2}</div>
                </div>
              </li>
              <li>
                <div>3. {promptText.step3Text1}</div>
                <div>
                  <img src={AppIcon} alt="App icon" className="pwa-prompts__appicon--small" />
                </div>
                <div>
                  <strong>{promptText.step3Text2}</strong>
                </div>
              </li>
            </>
          )}
        </ul>
      </div>
    </div>
  );
}

export default PWAIosPrompt;
